import {sameValue} from './comparison-utils';
import {FieldsEqualityComparers} from './equality-comparer';
import {
    AnyObject,
    ArrayPotentiallyReadonly,
    ArrayValues,
    DeepIndex,
    DeepKeyOf,
    Dictionary,
    NonNullishValue,
    OmitByValue,
    OmitStrict,
    PartiallyNotNullable,
    PartiallyRequired,
    RecordInferValue,
    Values
} from "./common-types/common-types";
import {arrayEqualInAnyOrder, arrayOrderBy} from "./array-utils";
import {compareStringsCaseSensitive} from "./comparers";
import {assertDefined, assertNotNullable} from "./assertions";
import {Evaluable, evaluateWhenFunction} from "./evaluable";
import {iterableMapToArray, iterableUnion} from "./iterable-utils/iterable-utils";

export function isObject (value: any) : value is object {
    return value != null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Inspired by 'fast-equal' package.
 */
export function objectDeepEqual<T>(
    a: T | null | undefined,
    b: T | null | undefined,
    ignoreEntriesWithUndefinedValues = false
) {
    if (a === b) return true;

    if (a && b && typeof a == 'object' && typeof b == 'object') {
        if ((a.constructor ?? Object) !== (b.constructor ?? Object)) return false;

        if (Array.isArray(a)) {

            if (!Array.isArray(b)) {
                return false;
            }

            const length = a.length;
            if (length != b.length) return false;
            for (let i = length; i-- !== 0;)
                if (!objectDeepEqual(a[i], b[i], ignoreEntriesWithUndefinedValues)) return false;
            return true;
        }


        if ((a instanceof Map) && (b instanceof Map)) {
            if (a.size !== b.size) {
                return false;
            }
            for (const i of a.entries()) {
                if (!b.has(i[0])) {
                    return false;
                }
            }
            for (const i of a.entries()) {
                if (!objectDeepEqual(i[1], b.get(i[0]), ignoreEntriesWithUndefinedValues)) {
                    return false;
                }
            }

            return true;
        }

        if ((a instanceof Set) && (b instanceof Set)) {
            if (a.size !== b.size) return false;
            for (const i of a.entries()) {
                if (!b.has(i[0])) return false;
            }
            return true;
        }

        // if (ArrayBuffer.isView(a) && ArrayBuffer.isView(b)) {
        //     length = a.length;
        //     if (length != b.length) return false;
        //     for (i = length; i-- !== 0;)
        //         if (a[i] !== b[i]) return false;
        //     return true;
        // }


        // if (a instanceof RegExp) return a.source === b.source && a.flags === b.flags;
        if (a.valueOf && b.valueOf && a.valueOf !== Object.prototype.valueOf) return a.valueOf() === b.valueOf();
        if (a.toString && b.toString && a.toString !== Object.prototype.toString) return a.toString() === b.toString();

        const keys = Object.keys(a);
        const length = keys.length;
        if (!ignoreEntriesWithUndefinedValues && length !== Object.keys(b).length) {
            return false
        }

        for (let i = length; i-- !== 0;) {
            const key = keys[i];
            if (!Object.prototype.hasOwnProperty.call(b, key) && (!ignoreEntriesWithUndefinedValues || (b as any)[key] !== undefined)) {
                return false;
            }
        }

        for (let i = length; i-- !== 0;) {
            const key = keys[i];

            if (!objectDeepEqual((a as any)[key], (b as any)[key], ignoreEntriesWithUndefinedValues)) {
                return false;
            }
        }

        return true;
    }

    // true if both NaN, false otherwise
    return a!==a && b!==b;
}

export function objectIsEmpty<T extends NonNullishValue> (obj: T) {
    return objectGetKeys(obj).length === 0;
}

export function objectFindNotDeepEqualFields<T extends object> (obj1: T, obj2: T) {
    const keys1 = objectGetKeys(obj1);
    const keys2 = objectGetKeys(obj2);
    const allKeys = iterableUnion(keys1, keys2);

    return allKeys.filter((key) => {
        return !objectDeepEqual(obj1[key], obj2[key]);
    })
}

/**
 * Performs equality by iterating through keys on an object and returning false
 * when any key has values which are not strictly equal between the arguments.
 * Returns true when the values of all keys are strictly equal.
 */
export function objectShallowEqual<T>(
    obj1: T | null | undefined,
    obj2: T | null | undefined,
    fieldsEqualityComparers?: Partial<FieldsEqualityComparers<T>>
): boolean {
    if (obj1 === null || obj2 === null || obj1 === undefined || obj2 === undefined) {
        return obj1 === obj2;
    }

    if (obj1 === obj2) {
        return true;
    }

    const keys1 = Object.keys(obj1) as (keyof T)[];
    const keys2 = Object.keys(obj2) as (keyof T)[];

    if (!arrayEqualInAnyOrder(keys1, keys2)) {
        return false;
    }

    // Test for A's keys different from B.
    for (const key of keys1) {
        const value1 = obj1[key];
        const value2 = obj2[key];

        const fieldEqualityComparer = fieldsEqualityComparers ? fieldsEqualityComparers[key] : undefined;
        if (fieldEqualityComparer) {
            if (!fieldEqualityComparer(value1, value2)) {
                return false;
            }
        } else {
            if (!sameValue(value1, value2)) {
                return false;
            }
        }
    }

    return true;
}

export function objectSortKeys<OBJ extends AnyObject> (
    obj: OBJ,
    comparer: (value1: string, value2: string) => number
) : OBJ {
    return objectFromEntries(arrayOrderBy(Object.entries(obj), ([key]) => key, comparer)) as OBJ
}

export function objectDeepSortKeys<OBJ extends AnyObject> (
    obj: OBJ,
    comparer: (value1: string, value2: string) => number
) : OBJ {

    return objectFromEntries(
        arrayOrderBy(
            Object.entries(obj).map(([key, value]) => [key, objectDeepSortKeysRec(value, comparer)]),
            ([key]) => key,
            comparer)
    ) as OBJ
}

export function objectStringificationEqual (obj1: AnyObject, obj2: AnyObject) {
    const str1 = JSON.stringify(objectDeepSortKeys(obj1, compareStringsCaseSensitive));
    const str2 = JSON.stringify(objectDeepSortKeys(obj2, compareStringsCaseSensitive));

    return str1 === str2;
}

function objectDeepSortKeysRec (
    value: any,
    comparer: (value1: string, value2: string) => number
) : any {

    if (typeof value === 'object' && value !== null) {
        if (Array.isArray(value)) {
            return value.map(arrayItem => {
                return objectDeepSortKeysRec(arrayItem, comparer)
            })
        } else {
            return objectDeepSortKeys(value, comparer)
        }
    } else {
        return value;
    }
}

export function objectForEachValue<VALUE>(
    obj: { [key: string]: VALUE },
    iterator: (value: VALUE, key: string) => void
) {
    const keys = Object.keys(obj);

    for (const key of keys) {
        iterator(obj[key], key);
    }
}

export function objectResolveUnequalFields<T extends Record<string, any>>(
    obj1: T,
    obj2: T,
    fieldsEqualityComparers?: Partial<FieldsEqualityComparers<T>>
): string[] {
    if (obj1 === obj2) {
        return [];
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    const result = [];

    const keysSet1 = new Set(keys1);
    const keysSet2 = new Set(keys2);

    const commonKeys = [];

    for (const key1 of keysSet1) {
        if (!keysSet2.has(key1)) {
            result.push(key1);
        } else {
            commonKeys.push(key1);
        }
    }

    for (const key2 of keysSet2) {
        if (!keysSet1.has(key2)) {
            result.push(key2);
        }
    }

    // Test for A's keys different from B.
    for (const key of commonKeys) {
        const value1 = obj1[key];
        const value2 = obj2[key];

        const fieldEqualityComparer = fieldsEqualityComparers ? fieldsEqualityComparers[key] : undefined;
        if (fieldEqualityComparer) {
            if (!fieldEqualityComparer(value1, value2)) {
                result.push(key);
            }
        } else {
            if (!sameValue(value1, value2)) {
                result.push(key);
            }
        }
    }

    return result;
}

export function objectResolveDifferencePaths(obj1: any, obj2: any, path = ''): string[] {
    const differences: string[] = [];

    const isObject = (value: any): boolean =>
        value !== null && typeof value === 'object' && !Array.isArray(value);

    if (isObject(obj1) && isObject(obj2)) {
        const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);
        for (const key of allKeys) {
            const newPath = path ? `${path}.${key}` : key;
            differences.push(...objectResolveDifferencePaths(obj1[key], obj2[key], newPath));
        }
    } else if (Array.isArray(obj1) && Array.isArray(obj2)) {
        const maxLength = Math.max(obj1.length, obj2.length);
        for (let i = 0; i < maxLength; i++) {
            const newPath = `${path}[${i}]`;
            differences.push(...objectResolveDifferencePaths(obj1[i], obj2[i], newPath));
        }
    } else if (obj1 !== obj2) {
        differences.push(path);
    }

    return differences;
}

export function objectGet<T extends object, K extends string> (obj: T, key: K) : K extends keyof T ? T[K] : Values<T> | undefined {
    return (obj as any)[key];
}

export function objectDeepGet<T, K extends DeepKeyOf<T>> (obj: T, deepKey: K) : DeepIndex<T, K> {
    const splittedDeepKey = (deepKey as string).split('.');

    let cursor: any = obj;
    for (const key of splittedDeepKey) {
        if (cursor === undefined || cursor === null) {
            return undefined as any;
        }

        cursor = cursor[key];
    }

    return cursor;
}

export function objectDeepSet<T, K extends DeepKeyOf<T>> (obj: T, deepKey: K, value: DeepIndex<T, K>) : void {
    const splittedDeepKey = (deepKey as string).split('.');
    const lastKey = assertDefined(splittedDeepKey.pop());

    let cursor: any = obj;
    for (const key of splittedDeepKey) {
        cursor = cursor[key];
        if (cursor === undefined) {
            return;
        }
    }

    cursor[lastKey] = value;
}

export function objectMapKeys (obj: object, mappingFunction: (key: string, value: any) => string) : object {

    const result: Record<string, any> = {};

    const keys = Object.keys(obj);
    const dynamicObj = obj as any;

    for (const key of keys)
    {
        const value = dynamicObj[key];
        const mappedKey = mappingFunction(key, value);

        if (result[mappedKey] !== undefined)
        {
            throw new Error();
        }

        result[mappedKey] = value && typeof value === 'object' && !Array.isArray(value) ?
            objectMapKeys(value, mappingFunction) :
            value;
    }

    return result;
}

export function objectMapEntries (obj: object, mappingFunction: (key: string, value: any) => ({key: string, value: any})) : object {

    if (Array.isArray(obj)) {
        return obj.map(item => objectMapEntries(item, mappingFunction));

    }

    const result: Record<string, any> = {};

    const keys = Object.keys(obj);
    const dynamicObj = obj as any;

    for (const key of keys)
    {
        const value = dynamicObj[key];
        const {key: mappedKey, value: mappedValue} = mappingFunction(key, value);

        if (result[mappedKey] !== undefined)
        {
            throw new Error();
        }

        result[mappedKey] = mappedValue && typeof mappedValue === 'object' && !Array.isArray(mappedValue) ?
            objectMapEntries(mappedValue, mappingFunction) :
            mappedValue;
    }

    return result;
}

const SkipToken = Symbol('SkipToken');
type SkipToken = typeof SkipToken;

export function objectMapValues<OBJ extends Record<any, any>, T2> (
    obj: OBJ,
    mappingFunction: (value: RecordInferValue<OBJ>, key: keyof OBJ & string, skip: typeof SkipToken) => (T2 | typeof SkipToken)
) : Record<keyof OBJ, T2> {

    const result = {} as Record<keyof OBJ, T2>;

    const keys = Object.keys(obj) as (keyof OBJ & string)[];
    const dynamicObj = obj as any;

    for (const key of keys)
    {
        const value = dynamicObj[key];
        const mappingResult = mappingFunction(value, key, SkipToken);
        if (mappingResult !== SkipToken) {
            result[key] = mappingResult;
        }
    }

    return result;
}

export function objectGetValues<T extends NonNullishValue> (obj: T) {
    return Object.values(obj) as Values<T>[];
}

export function objectGetKeys<T extends NonNullishValue> (obj: T) {
    return Object.keys(obj) as (keyof T & string)[]
}

export function objectGetEntries<T extends NonNullishValue> (obj: T) {
    return Object.entries(obj) as [keyof T & string, Values<T>][]
}

export function objectCreateFormMap<T> (map: Map<string, T>) : Record<string, T> {
    return objectCreateFromEntries(map.entries());
}

type ObjectOmitByValueResult<T extends NonNullishValue, V> =
    OmitByValue<{
        [K in keyof T]: Exclude<T[K], V>
    }, never>

export function objectOmitByValues<T extends NonNullishValue, V> (obj: T, values: V[]) : ObjectOmitByValueResult<T, V> {
    const result: any = {};

    const keys = Object.keys(obj) as (keyof T)[];

    for (const key of keys)
    {
        const currentValue = obj[key];
        if (!values.includes(currentValue as V)) {
            result[key] = currentValue;
        }
    }

    return result;
}

export function objectRemoveEntriesWithUndefinedValues<T extends NonNullishValue> (obj: T) : ObjectOmitByValueResult<T, undefined> {
    const result: any = {};

    const keys = Object.keys(obj) as (keyof T)[];

    for (const key of keys)
    {
        const currentValue = obj[key];
        if (currentValue !== undefined) {
            result[key] = currentValue;
        }
    }

    return result;
}

export function objectRemoveEntriesWithValue<T> (obj: Record<string, T>, value: T) {
    const result: Record<string, T> = {};

    const keys = Object.keys(obj);

    for (const key of keys)
    {
        const currentValue = obj[key];
        if (currentValue !== value) {
            result[key] = currentValue;
        }
    }

    return result;
}

export function objectRemoveEntries<T extends NonNullishValue> (
    obj: T,
    predicate: (value: Values<T>, key: keyof T) => boolean
) : Partial<T> {
    const result: any = {};

    const keys = Object.keys(obj) as (keyof T)[];

    for (const key of keys)
    {
        const currentValue = obj[key];
        if (!predicate(currentValue, key)) {
            result[key] = currentValue;
        }
    }

    return result;
}


export function objectFilterEntries<T> (obj: Record<string, T>, filter: (value: T, key: string) => boolean) {
    const result: Record<string, T> = {};

    const keys = Object.keys(obj);

    for (const key of keys)
    {
        const currentValue = obj[key];
        if (filter(currentValue, key)) {
            result[key] = currentValue;
        }
    }

    return result;
}

export function objectPick<T extends object, KEYS extends ArrayPotentiallyReadonly<Extract<keyof T, string>>> (obj: T, keys: KEYS) {
    const result: Dictionary<any> = {};

    const currentKeys = Object.keys(obj) as Extract<keyof T, string>[];
    for (const currentKey of currentKeys) {
        if (keys.includes(currentKey)) {
            result[currentKey] = obj[currentKey];
        }
    }

    return result as Pick<T, ArrayValues<KEYS>>;
}

export function objectAssertDefinedEntry<T, K extends keyof T & string> (
    obj: T,
    key: K
) {
    return assertDefined(obj[key], `Expected obj['${key}'] to be defined.`) as Exclude<T[K], undefined>
}

export function objectAssertNotNullableEntry<T, K extends keyof T & string> (
    obj: T,
    key: K
) {
    return assertNotNullable(obj[key], `Expected obj['${key}'] to be not nullable.`) as Exclude<T[K], null | undefined>
}

export function objectAssertDefinedEntries<T, K extends keyof T & string> (
    obj: T,
    keys: K[]
) {

    for (const key of keys) {
        assertDefined(obj[key], `Expected obj['${key}'] to be defined.`)
    }

    return obj as PartiallyRequired<T, K>;
}

export function objectAssertNotNullableEntries<T, K extends keyof T & string> (
    obj: T,
    keys: K[]
) {

    for (const key of keys) {
        assertNotNullable(obj[key], `Expected obj['${key}'] to be not nullable.`)
    }

    return obj as PartiallyNotNullable<T, K>;
}

export function objectAssign<T extends object> (obj: T, partialObj: Partial<T>) {
    Object.assign(obj, partialObj);

    return obj;
}

export function objectAssignAndPrune<T extends NonNullishValue, U extends NonNullishValue>(target: T, source: U): T & U {
    // Copy properties from the source to the target
    Object.assign(target, source);

    // Remove keys from the target that are not in the source
    Object.keys(target).forEach(key => {
        if (!(key in source)) {
            delete target[key as keyof typeof target];
        }
    });

    return target as T & U;
}

export const SyncableObjectValueType = {
    String: "string",
    Number: "number",
    Boolean: "boolean",
    Object: "object",
    Array: "array",
    Null: "null",
    Undefined: 'undefined'
} as const;

export type SyncableObjectValueType = Values<typeof SyncableObjectValueType>;

export type SyncableObject = {
    [key: string]: SyncableObjectValue
};

export type SyncableObjectValue = string | number | boolean | null | undefined | SyncableObject | SyncableObjectValue[];

export function objectResolveSyncableObjectValueType (value: unknown) : SyncableObjectValueType {
    if (value === null) {
        return SyncableObjectValueType.Null;
    }

    if (value === undefined) {
        return SyncableObjectValueType.Undefined;
    }

    if (Array.isArray(value)) {
        return SyncableObjectValueType.Array;
    }

    switch (typeof value) {
        case "string":
            return SyncableObjectValueType.String;
        case "number":
            return SyncableObjectValueType.Number;
        case "boolean":
            return SyncableObjectValueType.Boolean;
        case "object":
            return SyncableObjectValueType.Object;
        default:
            throw new Error(`Unknown JSON value type: ${value}`);
    }
}

export function objectDeepSync<T extends NonNullishValue, T2 extends NonNullishValue>(targetObj: T, sourceObj: T2): T2 {
    // Reference the target object directly
    const targetObjModified: any = targetObj;

    const sourceKeys = objectGetKeys(sourceObj);

    // Remove keys from targetObjModified that do not exist in sourceObj
    const targetKeys = objectGetKeys(targetObjModified);
    for (const key of targetKeys) {
        // Cast key to any to avoid TypeScript errors
        if (!sourceKeys.includes(key as any)) {
            delete targetObjModified[key];
        }
    }


    // Sync keys and values from sourceObj to targetObjModified
    for (const key of sourceKeys) {
        const sourceValue = sourceObj[key];
        const targetValue = targetObjModified[key];

        const sourceValueType = objectResolveSyncableObjectValueType(sourceValue);
        const targetValueType = objectResolveSyncableObjectValueType(targetValue);

        if (sourceValueType !== targetValueType) {
            targetObjModified[key] = sourceValue;
        } else {
            // If the source value is an object
            if (typeof sourceValue === 'object' && sourceValue !== null) {
                if (Array.isArray(sourceValue)) {
                    // If both sourceValue and targetValue are arrays, reuse the target array
                    // Sync the elements of the array
                    for (let i = 0; i < sourceValue.length; i++) {
                        // Check if sourceValue[i] is an object
                        if (typeof sourceValue[i] === 'object' && sourceValue[i] !== null) {
                            // Ensure targetValue has the same structure
                            if (i >= targetValue.length) {
                                targetValue[i] = {} as any; // Initialize if needed
                            }
                            // Check if targetValue[i] is an object before calling objectDeepSync
                            if (typeof targetValue[i] === 'object' && targetValue[i] !== null) {
                                objectDeepSync(targetValue[i] as any, sourceValue[i]); // Deep sync element
                            } else {
                                targetValue[i] = sourceValue[i]; // Replace with sourceValue[i] if not an object
                            }
                        } else {
                            targetValue[i] = sourceValue[i]; // Direct assignment for primitive values
                        }
                    }

                    // Truncate target array to match the length of source array
                    targetValue.length = sourceValue.length;
                } else {
                    // Recursively sync the objects only if sourceValue is an object
                    objectDeepSync(targetObjModified[key] as any, sourceValue);
                }
            } else {
                // Otherwise, directly assign the value
                targetObjModified[key] = sourceValue;
            }
        }
    }

    // Return the modified target object
    return targetObjModified;
}



export function objectOmit<T extends object, KEYS extends Extract<keyof T, string>[]> (obj: T, keys: KEYS) {
    const result: Dictionary<any> = {};

    const currentKeys = Object.keys(obj) as Extract<keyof T, string>[];
    for (const currentKey of currentKeys) {
        if (!keys.includes(currentKey)) {
            result[currentKey] = obj[currentKey];
        }
    }

    return result as Omit<T, ArrayValues<KEYS>>;
}

export function objectRemoveEntriesWithKeys<
    T extends NonNullishValue,
    KEY extends keyof T
>(obj: T, keys: KEY[]) : OmitStrict<T, KEY>{
    const result: any = {};

    const currentKeys = Object.keys(obj);

    for (const currentKey of currentKeys)
    {
        const key = currentKey as KEY;

        if (!keys.includes(key)) {
            result[key] = obj[key];
        }
    }

    return result;
}

/**
 * Strips the provided object from undefined values.
 * This function uses parse(stringify(...)) technique so it won't work for objects that can't be stringified
 * (e.g. with circular references).
 */
export function objectStrip<T extends object>(obj: T) : T {
    return JSON.parse(JSON.stringify(obj));
}

export function objectFromEntries<K extends string, T> (entries: Iterable<[K, T]>) : Record<K, T> {
    const result: Record<any, T> = {};
    for (const [key, value] of entries) {
        result[key] = value;
    }
    return result as Record<K, T>;
}

export function objectFromValues<T extends string>(values: T[]) : Record<T, T> {
    return objectFromEntries(values.map(value => [value, value]));
}

export function objectFromArray<T>(values: T[], keyFunction: (value: T) => string) : Record<string, T> {
    return objectFromEntries(values.map(value => [keyFunction(value), value]));
}

export function objectTryParse<T extends object = object> (str: string) : T | undefined {
    try {
        return JSON.parse(str);
    } catch (_error) {
        return undefined;
    }
}

export function objectCreate<T extends {}>(creationFunction: (skip: SkipToken) => T) : {
    [key in keyof OmitByValue<T, symbol>]: T[key]
} {

    const obj = creationFunction(SkipToken);

    return objectRemoveEntriesWithValue(obj, SkipToken) as any;
}

export function objectCreateFromEntries<K extends string | number | symbol, V>(entries: Iterable<[K, V]>) : Record<K, V> {
    const result = {} as Record<K, V>;

    for (const [key, value] of entries) {
        result[key] = value;
    }

    return result;
}

export function objectMapRecordValues<K extends string, V, V2>(
    record: Record<K, V>,
    mappingFunction: (value: V, key: K, removeEntry: iterableMapToArray.SkipToken) => V2
) : Record<K, V2> {

    return objectCreateFromEntries(iterableMapToArray(objectGetEntries(record), ([key, value], skip) => {

        const mappedValue = mappingFunction(value, key, skip);

        if (mappedValue === skip) {
            return skip;
        }

        return [key, mappedValue];
    }));
}

export function objectCreateFromKeys<K extends string, V>(keys: readonly K[], value: Evaluable<(key: K) => V>) : Record<K, V> {
    return objectCreateFromEntries(keys.map(key => {
        return [
            key,
            evaluateWhenFunction(value, key)
        ]
    }))
}
